import {useThreads} from '../stores/selectors'
import {type Thread, CommentStates} from '@github-ui/diff-lines/conversation-types'
import {useMemo} from 'react'
import type {PendingComment} from '../page-data/payloads/thread-previews'
import {
  positionedThreadToSubject,
  useThreadPreviewPositioning,
} from '../page-data/loaders/use-thread-preview-positioning'
import {useHydrateThreadPositioning} from './use-hydrate-thread-positioning'

/**
 * Helper function to extract pending comments from threads
 */
function extractPendingCommentsFromThreads(threads: Thread[]): PendingComment[] {
  const comments: PendingComment[] = []

  for (const thread of threads) {
    // Get pending comments from this thread
    const threadPendingComments = (thread.commentsData?.comments ?? []).filter(
      comment => comment.state === CommentStates.Pending,
    )

    for (const currentComment of threadPendingComments) {
      if (!currentComment.databaseId) {
        continue
      }

      comments.push({
        commentID: currentComment.databaseId,
        thread,
      })
    }
  }

  return comments
}

/**
 * Hook for getting pending review thread previews using store-based positioning (new approach)
 * Uses the useHydrateThreadPositioning hook to apply positioning data to threads in the store
 */
export function useGetPendingCommentsFromStore(pendingReviewIds?: number[]): PendingComment[] {
  const threads = useThreads(pendingReviewIds)

  useHydrateThreadPositioning(threads)

  // Extract pending comments from threads with positioning already applied
  const commentsList = useMemo(() => {
    if (!pendingReviewIds || pendingReviewIds.length === 0 || !threads) {
      return []
    }

    return extractPendingCommentsFromThreads(threads)
  }, [threads, pendingReviewIds])

  return commentsList
}

export function useGetPendingCommentsFromStoreWithoutDiffEntries(pendingReviewIds?: number[]): PendingComment[] {
  const threads = useThreads(pendingReviewIds)

  useHydrateThreadPositioning(threads)

  // Extract pending comments from threads with positioning already applied
  const commentsList = useMemo(() => {
    if (!pendingReviewIds || pendingReviewIds.length === 0 || !threads) {
      return []
    }

    return extractPendingCommentsFromThreads(threads)
  }, [threads, pendingReviewIds])

  return commentsList
}

/**
 * Hook for getting pending review thread previews with positioning data stitched in (legacy approach)
 * Returns PendingComment format with the full Thread object for each pending comment
 */
export function useGetPendingComments(pendingReviewIds?: number[]): PendingComment[] {
  const threads = useThreads(pendingReviewIds)

  const {pendingComments, threadsNeedingSubject} = useMemo(() => {
    if (!pendingReviewIds || pendingReviewIds.length === 0 || !threads) {
      return {pendingComments: [], threadsNeedingSubject: []}
    }

    return {
      pendingComments: extractPendingCommentsFromThreads(threads),
      threadsNeedingSubject: threads.filter(thread => !thread.subject?.diffLines).map(thread => Number(thread.id)),
    }
  }, [threads, pendingReviewIds])

  const threadPreviewPositioningMap = useThreadPreviewPositioning(threadsNeedingSubject).data

  const stitchedComments = useMemo(() => {
    if (threadsNeedingSubject.length === 0 || !threadPreviewPositioningMap) {
      return pendingComments
    }

    // Create new objects instead of mutating to trigger React re-renders
    return pendingComments.map(pendingComment => {
      const threadId = pendingComment.thread.id

      const threadPreviewPositioning = threadPreviewPositioningMap.threads[threadId]
      if (!threadPreviewPositioning) {
        return pendingComment
      }

      return {
        ...pendingComment,
        thread: {
          ...pendingComment.thread,
          path: threadPreviewPositioning.path ?? pendingComment.thread.path,
          subject: positionedThreadToSubject(threadPreviewPositioning),
        },
      }
    })
  }, [pendingComments, threadsNeedingSubject, threadPreviewPositioningMap])

  return stitchedComments
}
